import React, { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import Chart from "react-apexcharts";
import { useGetDashboardStats } from "../../../APIS";
import { useEticketingDashboardFilterOptionsQuery } from "../../../api/e-ticketing/e-ticketing.hooks";
import { AbilityContext } from "../../../context/Can";
import { getCurrentUser } from "../PluginsMenu/Nestable/utils";
import DownloadReportButton from "../../pages/Dashboard/_components/DownloadReportButton";
import {
  buildReportTitle,
  getCityName,
  getServiceName,
} from "../../pages/Dashboard/_components/reportTitle";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => `PKR ${toNumber(value).toLocaleString()}`;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const getChartData = (tableData) => {
  const categories = tableData.map((row) => row.category);
  const quantities = tableData.map((row) => toNumber(row.quantity));

  return {
    series: [
      {
        name: "Total Reservations",
        data: quantities,
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
        toolbar: {
          show: true,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 4,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: "12px",
          colors: ["#304758"],
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories,
        title: {
          text: "Pricing Tiers",
        },
      },
      yaxis: {
        title: {
          text: "Total Reservations (Tickets)",
        },
      },
      fill: {
        opacity: 1,
        colors: ["#4cbc9a"],
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toLocaleString() + " tickets";
          },
        },
      },
    },
  };
};

const TopTabsRow = ({ topTabs }) => {
  if (!topTabs) return null;

  return (
    <Row className="g-3 mb-4">
      <Col xl={3} md={6}>
        <Card className="h-100">
          <Card.Body>
            <div className="text-muted small">Total Reservations</div>
            <div className="text-muted small mb-2">
              ({toNumber(topTabs.totalReservations).toLocaleString()} Tickets)
            </div>
            <h4 className="mb-0">{formatCurrency(topTabs.totalReservationsAmount)}</h4>
          </Card.Body>
        </Card>
      </Col>
      <Col xl={3} md={6}>
        <Card className="h-100">
          <Card.Body>
            <div className="text-muted small">Total Refund</div>
            <div className="text-muted small mb-2">
              ({toNumber(topTabs.totalRefund).toLocaleString()} Tickets)
            </div>
            <h4 className="mb-0">{formatCurrency(topTabs.totalRefundAmount)}</h4>
          </Card.Body>
        </Card>
      </Col>
      <Col xl={3} md={6}>
        <Card className="h-100">
          <Card.Body>
            <div className="text-muted small">Free Tier Tickets</div>
            <div className="text-muted small mb-2">
              ({toNumber(topTabs.freeTierTickets).toLocaleString()} Tickets)
            </div>
            <h4 className="mb-0">{formatCurrency(topTabs.freeTierTicketsAmount)}</h4>
          </Card.Body>
        </Card>
      </Col>
      <Col xl={3} md={6}>
        <Card className="h-100">
          <Card.Body>
            <div className="text-muted small">Net Revenue</div>
            <div className="text-muted small mb-2">(Revenue)</div>
            <h4 className="mb-0">{formatCurrency(topTabs.netRevenue)}</h4>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

const SectionCard = ({ section, showTopTabs = true }) => {
  const { title, topTabs, tableData = [] } = section;
  const chartData = useMemo(() => getChartData(tableData), [tableData]);

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">{title}</Card.Title>
        <small className="text-muted">{tableData.length} pricing entries</small>
      </Card.Header>
      <Card.Body>
        {showTopTabs && topTabs && <TopTabsRow topTabs={topTabs} />}

        {tableData.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Ticket Category</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={`${row.category}-${index}`}>
                    <td>{row.category}</td>
                    <td>{formatCurrency(row.price)}</td>
                    <td>{toNumber(row.quantity).toLocaleString()}</td>
                    <td>{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
                {topTabs && (
                  <>
                    <tr style={{ fontWeight: 500 }}>
                      <td>Sub Total</td>
                      <td></td>
                      <td>{toNumber(topTabs.totalReservations).toLocaleString()}</td>
                      <td>{formatCurrency(topTabs.totalReservationsAmount)}</td>
                    </tr>
                    <tr style={{ fontWeight: 500 }}>
                      <td>Refunded</td>
                      <td></td>
                      <td>{toNumber(topTabs.totalRefund).toLocaleString()}</td>
                      <td>{formatCurrency(topTabs.totalRefundAmount)}</td>
                    </tr>
                    <tr style={{ fontWeight: 500 }}>
                      <td>Total</td>
                      <td></td>
                      <td>
                        {toNumber(topTabs.totalReservations - topTabs.totalRefund).toLocaleString()}
                      </td>
                      <td>{formatCurrency(topTabs.netRevenue)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted mb-0">No pricing data found for selected range.</p>
        )}

        {tableData.length > 0 && (
          <div className="mt-4">
            <Chart options={chartData.options} series={chartData.series} type="bar" height={350} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const EticketDashboard = () => {
  const ability = useContext(AbilityContext);
  const isGlobalUser = ability.can("manage", "all");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceId = searchParams.get("service_id");

  const monthRange = useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = useState(monthRange.startDate);
  const [endDate, setEndDate] = useState(monthRange.endDate);
  const [selectedCityId, setSelectedCityId] = useState("");

  const { city_id } = getCurrentUser() || {};
  const effectiveCityId = isGlobalUser ? selectedCityId || undefined : city_id;
  const cityIdForTitle = selectedCityId || city_id;

  const { data: stats, isLoading, isError, error } = useGetDashboardStats(
    serviceId,
    startDate,
    endDate,
    effectiveCityId
  );

  const { data: filterOptions, isLoading: filterOptionsLoading } =
    useEticketingDashboardFilterOptionsQuery({
      enabled: isGlobalUser || Boolean(city_id),
    });

  const citiesOptions = filterOptions?.data?.cities || [];
  const serviceName = getServiceName(serviceId);
  const cityName = getCityName(cityIdForTitle, citiesOptions);
  const reportTitle = buildReportTitle(serviceName, cityName, startDate, endDate);
  const sections = stats?.sections || [];
  const overallSection = sections.find((section) => section.type === "overall");

  if (isLoading) {
    return (
      <div className="container-fluid">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <Card>
          <Card.Body>
            <Alert variant="danger" className="mb-0">
              Failed to load dashboard stats.{" "}
              {error?.response?.data?.message || "Please try again."}
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="mb-0">Date Range Filter</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Col>
            {isGlobalUser && (
              <Col md={3}>
                <Form.Label>City</Form.Label>
                <Form.Select
                  value={selectedCityId}
                  onChange={(event) => setSelectedCityId(event.target.value)}
                  disabled={filterOptionsLoading}
                >
                  <option value="">All Cities</option>
                  {citiesOptions.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}
            <Col md={4}>
              <DownloadReportButton
                contentSelector="#eticket-dashboard-report-content"
                filename={reportTitle}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div id="eticket-dashboard-report-content">
        <h3 className="mb-4">{reportTitle}</h3>
        {overallSection?.topTabs && <TopTabsRow topTabs={overallSection.topTabs} />}

        {sections.length > 0 ? (
          sections.map((section) => (
            <SectionCard
              key={`${section.type}-${section.route_id ?? section.title}`}
              section={section}
              showTopTabs={section.type !== "overall"}
            />
          ))
        ) : (
          <Card className="mb-4">
            <Card.Body>
              <p className="text-muted mb-0">No dashboard data found for selected range.</p>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EticketDashboard;
